import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAudioSystem } from "../context/AudioContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import StatusBar from "../components/StatusBar";
import MusicSection from "../components/MusicSection";
import Soundboard from "../components/Soundboard";
import VolumeControl from "../components/VolumeControl";
import StopAllButton from "../components/StopAllButton";
import EmergencyModal from "../components/EmergencyModal";
import { Upload, ListMusic, Users as UsersIcon, ScrollText, Settings as Cog, LogOut, Gamepad2, IdCard, MessageSquare } from "lucide-react";

export default function Dashboard() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { playPriority, prefetchCritical } = useAudioSystem();
  const [files, setFiles] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.get("/audio"), api.get("/playlists")]);
      setFiles(a.data);
      setPlaylists(p.data);
      prefetchCritical(a.data.filter((f) => f.is_critical || f.category === "Emergency"));
    } catch (e) {
      toast.error("Failed to load audio system");
    }
  }, [prefetchCritical]);

  useEffect(() => { load(); }, [load]);

  const trigger = (file) => {
    if (file.confirmation_required && !file.immediate_activation) {
      setPending(file);
    } else {
      doPlay(file);
    }
  };

  const doPlay = (file) => {
    setPending(null);
    playPriority(file);
    toast.error(`PRIORITY: ${file.name}`, { duration: 4000 });
  };

  const navBtns = [
    { to: "/games", label: "GAMES CONTROL", icon: Gamepad2, show: true },
    { to: "/memberships", label: "MEMBERSHIPS", icon: IdCard, show: true },
    { to: "/messages", label: "MESSAGES", icon: MessageSquare, show: true },
    { to: "/audit", label: "AUDIT LOG", icon: ScrollText, show: true },
    { to: "/manage-audio", label: "MANAGE AUDIO", icon: Upload, show: isAdmin },
    { to: "/playlists", label: "PLAYLISTS", icon: ListMusic, show: isAdmin },
    { to: "/users", label: "USERS", icon: UsersIcon, show: isAdmin },
    { to: "/settings", label: "SETTINGS", icon: Cog, show: isAdmin },
  ].filter((b) => b.show);

  return (
    <div className="min-h-screen w-full bg-[#09090B] p-4 md:p-6 yaz-scroll">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight text-white leading-none">
            YAZ WORKINGTON
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="font-display font-bold text-sm tracking-[0.2em] text-[#00F0FF]">MANAGEMENT SYSTEM</p>
            <span className="px-2 py-0.5 rounded-full bg-[#FF007F] text-white font-display font-bold text-[10px] tracking-widest">AUDIO</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right mr-1 hidden sm:block">
            <div className="font-bold text-white leading-none">{user?.name}</div>
            <div className="text-xs text-[#71717A] uppercase tracking-widest">{user?.role}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); navigate("/login"); }}
            className="h-12 px-4 rounded-xl bg-[#27272A] hover:bg-[#3f3f46] text-white font-bold flex items-center gap-2 active:scale-95 transition-transform duration-75"
          ><LogOut className="w-5 h-5" /> EXIT</button>
        </div>
      </div>

      <div className="mb-5"><StatusBar /></div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2"><MusicSection playlists={playlists} /></div>
        <div className="flex flex-col gap-5">
          <VolumeControl />
          <div className="flex-1 min-h-[9rem]"><StopAllButton /></div>
        </div>
      </div>

      {/* Soundboard */}
      <div className="mb-6"><Soundboard files={files} onTrigger={trigger} /></div>

      {/* Admin / nav */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {navBtns.map((b) => (
          <button
            key={b.to}
            data-testid={`nav-${b.to.replace("/", "")}`}
            onClick={() => navigate(b.to)}
            className="h-16 rounded-xl bg-[#18181B] border border-white/10 hover:bg-[#27272A] text-white font-display font-bold text-sm tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform duration-75"
          ><b.icon className="w-5 h-5 text-[#FF007F]" /> {b.label}</button>
        ))}
      </div>

      <EmergencyModal file={pending} onCancel={() => setPending(null)} onConfirm={() => doPlay(pending)} />
    </div>
  );
}
